import React from 'react';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

interface CampaignMetricItemProps {
  icon: IconName;
  label: string;
  value: number;
  total?: number;
  percentage?: number;
  isError?: boolean;
  color?: string;
}

/**
 * Campaign Metric Item Component
 * Displays a single metric with icon and optional percentage
 *
 * @example
 * <CampaignMetricItem
 *   icon={Send}
 *   label="Enviados"
 *   value={850}
 *   total={1000}
 * />
 */
export const CampaignMetricItem: React.FC<CampaignMetricItemProps> = ({
  icon,
  label,
  value,
  total,
  percentage,
  isError = false,
  color,
}) => {
  // Calculate percentage, handling NaN and edge cases
  let displayPercentage: number | undefined;
  if (percentage !== undefined && !isNaN(percentage)) {
    displayPercentage = percentage;
  } else if (total !== undefined && total > 0) {
    const calculated = (value / total) * 100;
    displayPercentage = isNaN(calculated) ? undefined : calculated;
  } else {
    displayPercentage = undefined;
  }

  const metricColor = isError
    ? 'var(--theme-error-color)'
    : color || 'var(--theme-card-text)';

  return (
    <div className="flex items-start gap-2">
      <div
        className="p-1.5 rounded-lg flex-shrink-0"
        style={{
          backgroundColor: isError
            ? 'var(--theme-error-background)'
            : 'var(--theme-button-ghost-hover)',
          color: metricColor,
        }}
      >
        <Icon name={icon} size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {label}
        </p>
        <div className="flex items-baseline gap-1.5">
          <p className="text-sm font-semibold" style={{ color: metricColor }}>
            {(value || 0).toLocaleString()}
          </p>
          {displayPercentage !== undefined && (
            <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              ({displayPercentage.toFixed(1)}%)
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
